import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Phone } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Props {
  ownerId: string;
  ownerName?: string;
  onClose: () => void;
  /** Called with the saved phone after it's persisted to the contact. */
  onSaved: (phone: string) => void;
}

// Prompts for an owner's WhatsApp number when none is on file, saves it to the
// contact, and hands it back so the caller can continue (e.g. open WhatsApp).
export function PhonePromptModal({ ownerId, ownerName, onClose, onSaved }: Props) {
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const clean = phone.trim();
    if (clean.replace(/\D/g, '').length < 8) {
      setError('Ingresa un número de teléfono válido.');
      return;
    }
    if (!supabase) { setError('Supabase no inicializado'); return; }
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.from('contacts').update({ phone: clean }).eq('id', ownerId);
    setSaving(false);
    if (err) { setError('No se pudo guardar el teléfono.'); return; }
    onSaved(clean);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-black text-slate-900">Teléfono del propietario</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-500">
            {ownerName ? `${ownerName} no tiene` : 'Este propietario no tiene'} un número guardado. Ingrésalo para enviar el contrato por WhatsApp; se guardará en su ficha.
          </p>
          <div className="relative">
            <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="tel" inputMode="tel" autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              placeholder="5555 5555"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-bold focus:outline-none focus:border-indigo-500"
            />
          </div>
          {error && <p className="text-sm font-medium text-rose-600 bg-rose-50 rounded-xl px-3 py-2">{error}</p>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-black text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? <Loader2 className="animate-spin" size={18} /> : null}
            {saving ? 'Guardando…' : 'Guardar y continuar'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
